// The version badge (bottom-left, every screen) + the logs modal it opens.
//
// Brief 5 finding (2026-07-10): the founder asked for a build stamp on every
// screen, clickable to reveal the device's logs with a copy button — so a
// tester can hand over exactly what happened. Canonical across all Waytide
// apps; this is the WEB implementation (the desktop has its own copy in its
// edge — same label format, same modal).
//
// The label reads e.g. "Staging 0.1.0 (12)":
//   - env      ← VITE_DEPLOY_ENV (staging | production), title-cased
//   - version  ← VITE_APP_VERSION (the product version, baked at build)
//   - build    ← VITE_BUILD_NUMBER (the deploy run number; "dev" locally)

import { useEffect, useState } from "react";
import {
  getLogLines,
  logsAsText,
  subscribeLogs,
  type LogLine,
} from "./logBuffer";

const ENV = ((import.meta.env.VITE_DEPLOY_ENV as string) || "dev").toLowerCase();
const VERSION = (import.meta.env.VITE_APP_VERSION as string) || "0.0.0";
const BUILD = (import.meta.env.VITE_BUILD_NUMBER as string) || "dev";

/** "staging" → "Staging". */
function titleCase(s: string): string {
  return s ? s[0].toUpperCase() + s.slice(1) : s;
}

/** The full stamp, e.g. "Staging 0.1.0 (12)". Exported for reuse/tests. */
export function versionLabel(): string {
  return `${titleCase(ENV)} ${VERSION} (${BUILD})`;
}

export function VersionBadge() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        className="wt-versionbadge"
        onClick={() => setOpen(true)}
        title="Build info & logs"
      >
        {versionLabel()}
      </button>
      {open ? <LogsModal onClose={() => setOpen(false)} /> : null}
    </>
  );
}

function LogsModal({ onClose }: { onClose: () => void }) {
  const [lines, setLines] = useState<LogLine[]>(getLogLines());
  const [copied, setCopied] = useState(false);

  // Live-follow the buffer while the modal is open.
  useEffect(() => subscribeLogs(() => setLines(getLogLines())), []);

  // Close on Escape.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function copy() {
    const text = `${versionLabel()}\n\n${logsAsText()}`;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Fallback for contexts without the async clipboard API.
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  return (
    <div
      className="wt-logs__overlay"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="wt-logs__panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Build info and logs"
      >
        <div className="wt-logs__head">
          <span className="wt-logs__title">{versionLabel()}</span>
          <div className="wt-logs__actions">
            <button type="button" className="wt-logs__btn" onClick={copy}>
              {copied ? "Copied" : "Copy logs"}
            </button>
            <button type="button" className="wt-logs__btn" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
        <pre className="wt-logs__body">
          {lines.length === 0
            ? "No logs yet."
            : lines
                .map(
                  (l) =>
                    `${new Date(l.t).toLocaleTimeString()} [${l.level}] ${l.msg}`,
                )
                .join("\n")}
        </pre>
      </div>
    </div>
  );
}
